import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownData, DynamicRouteHandlingService, InputFieldComponent, LookupsService } from '@zayan-erp/shared';
import { GenericDropdownComponent } from '@zayan-erp/shared';
import { MinorCodesService } from '../../services/minor-codes.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { JobTypeService } from '../../services/job-type.service';
import { map, of, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MinorCode } from '../../models/minor-codes.model';
import { MinorCodeCategory, MinorCodeType } from '../../enums/minor-codes.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'minor-code-form',
  imports: [ReactiveFormsModule, InputFieldComponent, GenericDropdownComponent, ToggleSwitch],
  templateUrl: './minor-code-form.component.html',
  styles: [``]
})
export class MinorCodeFormComponent {
  minorCodeService = inject(MinorCodesService);
  jobTypeService = inject(JobTypeService);
  activatedRoute = inject(ActivatedRoute);
  dynamicRouteHandling = inject(DynamicRouteHandlingService);
  destroyRef = inject(DestroyRef);
  lookupsService = inject(LookupsService);
  router = inject(Router);
  modalService = inject(NgbModal);

  constructor() {
    this.dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onFormSubmit();
    });
  }

  jobTypeQueryParam = toSignal(this.activatedRoute.queryParamMap.pipe(map(params => params.get('jobTypeId'))), {
    initialValue: null
  });

  onJobTypeQueryParamChange = effect(() => {
    this.minorCodeForm.patchValue({
      jobTypeId: this.jobTypeQueryParam()
    });
  });

  uofmList = toSignal(
    this.lookupsService
      .readUofm()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.description, secondLabel: item.code }) as DropdownData)
        )
      ),
    { initialValue: [] }
  );
  minorCodesStatusList = toSignal(this.minorCodeService.getMinorCodeStatusList(), { initialValue: [] });
  minorCodesCategoryList = toSignal(this.minorCodeService.getMinorCodeCategoryList(), { initialValue: [] });
  jobTypeList = toSignal(
    this.jobTypeService
      .readJobTypes()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.abbreviation }) as DropdownData)
        )
      ),
    { initialValue: [] }
  );

  idToEdit = signal<string | null>(this.activatedRoute.snapshot.paramMap.get('id'));

  recordToEditRxResource = rxResourceFixed({
    params: () => ({ id: this.idToEdit() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.minorCodeService.readMinorCodeById(params.id).pipe(
          tap(minorCode => {
            this.onDataLoaded(minorCode);
          })
        );
      }
      return of(null);
    }
  });

  onIdToEditChange = effect(() => {
    if (this.idToEdit()) {
      this.minorCodeForm.controls.jobTypeId.disable();
    } else {
      this.minorCodeForm.controls.jobTypeId.enable();
    }
  });

  minorCodeForm = new FormGroup({
    jobTypeId: new FormControl<string | undefined>(undefined, Validators.required),
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required),
    abbreviation: new FormControl<string | null>(null),
    type: new FormControl<string | null>(null, Validators.required),
    category: new FormControl<string | null>(null, Validators.required),
    unitOfMeasureId: new FormControl<string | null>(null),
    quantityRequired: new FormControl(false),
    unitPriceRequired: new FormControl(false),
    hourRequired: new FormControl(false),
    amountRequired: new FormControl(false),
    isActive: new FormControl(false)
  });

  onFormSubmit(): void {
    if (this.minorCodeForm.invalid) {
      this.minorCodeForm.markAllAsTouched();
      return;
    }

    if (!this.idToEdit()) this.createMinorCode();
    else this.updateMinorCode();
  }

  createMinorCode() {
    const minorCode = this.minorCodeForm.value as unknown as Omit<MinorCode, 'id'>;
    const sanitizedMinorCode = this.sanitizeMinorCode(minorCode);

    this.minorCodeService.createMinorCode(sanitizedMinorCode).subscribe(res => {
      this.afterFormSubmitSuccess();
    });
  }

  onDataLoaded(minorCode: Omit<MinorCode, 'id'>) {
    this.minorCodeForm.patchValue({
      jobTypeId: minorCode.jobTypeId,
      code: minorCode.code,
      description: minorCode.description,
      abbreviation: minorCode.abbreviation,
      type: minorCode.type.toString(),
      category: minorCode.category.toString(),
      unitOfMeasureId: minorCode.unitOfMeasureId,
      quantityRequired: minorCode.quantityRequired,
      unitPriceRequired: minorCode.unitPriceRequired,
      hourRequired: minorCode.hourRequired,
      amountRequired: minorCode.amountRequired,
      isActive: minorCode.isActive
    });
  }

  updateMinorCode() {
    const minorCode = this.minorCodeForm.value as unknown as Omit<MinorCode, 'id'>;
    const sanitizedMinorCode = this.sanitizeMinorCode(minorCode);

    this.minorCodeService.updateMinorCode(sanitizedMinorCode, this.idToEdit()!).subscribe(res => {
      this.afterFormSubmitSuccess();
    });
  }

  sanitizeMinorCode(minorCode: Omit<MinorCode, 'id'>): Omit<MinorCode, 'id'> {
    if (minorCode.type) {
      minorCode.type = parseInt(minorCode.type as unknown as string) as MinorCodeType;
    }
    if (minorCode.category) {
      minorCode.category = parseInt(minorCode.category as unknown as string) as MinorCodeCategory;
    }
    return { ...minorCode };
  }

  afterFormSubmitSuccess() {
    this.dynamicRouteHandling.reload.next(!this.dynamicRouteHandling.reload.value);
    this.modalService.dismissAll();
    this.router.navigate([this.activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
  }
}
